import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil } from 'lucide-react';
import { z } from 'zod';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { formatTaxSuffix } from '@/utils/tax';
import { useServiceCatalog } from '@/hooks/useReferenceData';
import type { ServiceCatalog as Service } from '@/types';

const serviceSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  description: z.string().optional(),
  default_price: z.number().min(0, 'Price cannot be negative').optional(),
});

export function ServiceCatalog() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultPrice, setDefaultPrice] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { locale, formatMoney } = useCompanyLocale();
  const tax = useCompanyTax();

  const { data: services = [], isLoading } = useServiceCatalog(false);

  const toRow = (values: z.infer<typeof serviceSchema>) => ({
    name: values.name,
    description: values.description?.trim() || null,
    default_price: values.default_price ?? null,
  });

  const createMutation = useMutation({
    mutationFn: async (values: z.infer<typeof serviceSchema>) => {
      const { error } = await supabase.from('service_catalog').insert(toRow(values));
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-catalog'] });
      toast({ title: 'Service created successfully' });
      resetForm();
    },
    onError: (error) => {
      toast({ title: 'Error creating service', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: z.infer<typeof serviceSchema> }) => {
      const { error } = await supabase.from('service_catalog').update(toRow(values)).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-catalog'] });
      toast({ title: 'Service updated successfully' });
      resetForm();
    },
    onError: (error) => {
      toast({ title: 'Error updating service', description: error.message, variant: 'destructive' });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('service_catalog')
        .update({ is_active: !isActive })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-catalog'] });
      toast({ title: 'Service status updated' });
    },
    onError: (error) => {
      toast({ title: 'Error updating service', description: error.message, variant: 'destructive' });
    },
  });

  const resetForm = () => {
    setName('');
    setDescription('');
    setDefaultPrice('');
    setEditingService(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const validated = serviceSchema.parse({
        name,
        description,
        default_price: defaultPrice ? parseFloat(defaultPrice) : undefined,
      });
      if (editingService) {
        updateMutation.mutate({ id: editingService.id, values: validated });
      } else {
        createMutation.mutate(validated);
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({ title: 'Validation error', description: error.errors[0].message, variant: 'destructive' });
      }
    }
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setName(service.name);
    setDescription(service.description || '');
    setDefaultPrice(service.default_price?.toString() || '');
    setIsDialogOpen(true);
  };

  const priceSuffix = tax.enabled ? ` (${formatTaxSuffix(tax)})` : '';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Services</h3>
          <p className="text-sm text-muted-foreground">
            Services you charge for, like a call-out fee or an hour of labour. Pick them onto quotes and invoices.
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Add Service
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingService ? 'Edit Service' : 'New Service'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="service-name">Service Name *</Label>
                <Input
                  id="service-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Call-out fee"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-description">Description</Label>
                <Textarea
                  id="service-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Internal note on what this covers (optional)"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-price">Default Price ({locale.currency}){priceSuffix}</Label>
                <Input
                  id="service-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={defaultPrice}
                  onChange={(e) => setDefaultPrice(e.target.value)}
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to price it on each quote or invoice. Editable per line either way.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {editingService ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : services.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No services yet. Click "Add Service" to create one.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Service Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Default Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {services.map((service) => (
              <TableRow key={service.id}>
                <TableCell className="font-medium">{service.name}</TableCell>
                <TableCell className="max-w-[240px] truncate text-muted-foreground">
                  {service.description || '-'}
                </TableCell>
                <TableCell>
                  {service.default_price != null ? formatMoney(service.default_price) : '-'}
                </TableCell>
                <TableCell>
                  <Badge variant={service.is_active ? 'default' : 'secondary'}>
                    {service.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(service)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActiveMutation.mutate({ id: service.id, isActive: service.is_active })}
                    >
                      {service.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
